import React, { useState, useRef } from 'react';
import emailjs from '@emailjs/browser';
import './ContactUs.css';
import { CONTACT_INFO, EMAILJS_CONFIG } from '../constants';
import SEO from '../components/SEO';

function ContactUs() {
  const form = useRef();
  const [isSending, setIsSending] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const phoneNumber = CONTACT_INFO.PHONE.VALUE;
    const whatsappMessage = `*New Contact Request*\n\n` +
      `*Name:* ${formData.name}\n` +
      `*Email:* ${formData.email}\n` +
      `*Phone:* ${formData.phone}\n` +
      `*Subject:* ${formData.subject}\n` +
      `*Message:* ${formData.message}`;
      
    window.open(`https://wa.me/${phoneNumber}?text=${encodeURIComponent(whatsappMessage)}`, '_blank');

    setFormData({
      name: '',
      email: '',
      phone: '',
      subject: '',
      message: ''
    });
  };

  return (
    <div className="contact-page">
      <SEO 
        title="Contact Us - BookMyNewsAd Support"
        description="Contact BookMyNewsAd for help with booking newspaper ads. Call us at +91 98765 43210 or email info@bookmynewsad.com. 24/7 Support available."
        url="/contact"
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Contact Us', url: '/contact' }
        ]}
      />
      <section className="contact-hero">
        <div className="container">
          <h1>Contact Us</h1>
          <p className="lead">We're here to help you 24/7</p>
        </div>
      </section>

      <section className="contact-content">
        <div className="container">
          <div className="contact-grid">
            <div className="contact-info">
              <h2>Get In Touch</h2>
              <p>Have questions? We'd love to hear from you. Send us a message and we'll respond as soon as possible.</p>
              
              <div className="contact-details">
                <div className="contact-item">
                  <div className="contact-icon">📞</div>
                  <div>
                    <h3>Phone</h3>
                    <p>
                      <a href={`tel:${CONTACT_INFO.PHONE.VALUE}`} className="phone-link">
                        {CONTACT_INFO.PHONE.DISPLAY}
                      </a>
                    </p>
                    <p className="small">Mon-Sun: 24/7 Available</p>
                  </div>
                </div>

                <div className="contact-item">
                  <div className="contact-icon">✉️</div>
                  <div>
                    <h3>Email</h3>
                    <p>
                      <a href={`mailto:${CONTACT_INFO.EMAIL}`} className="email-link">
                        {CONTACT_INFO.EMAIL}
                      </a>
                    </p>
                    <p>
                      <a href={`mailto:${CONTACT_INFO.SUPPORT_EMAIL}`} className="email-link">
                        {CONTACT_INFO.SUPPORT_EMAIL}
                      </a>
                    </p>
                  </div>
                </div>

                <div className="contact-item">
                  <div className="contact-icon">📍</div>
                  <div>
                    <h3>Office</h3>
                    <p>{CONTACT_INFO.ADDRESS.LINE1}</p>
                    <p>{CONTACT_INFO.ADDRESS.LINE2}</p>
                  </div>
                </div>

                <div className="contact-item">
                  <div className="contact-icon">💬</div>
                  <div>
                    <h3>WhatsApp</h3>
                    <p>{CONTACT_INFO.PHONE.DISPLAY}</p>
                    <a 
                      href={`https://wa.me/${CONTACT_INFO.PHONE.VALUE}`}
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="whatsapp-link"
                    >
                      Chat with us
                    </a>
                  </div>
                </div>

                <div className="contact-item">
                  <div className="contact-icon">🌐</div>
                  <div>
                    <h3>Follow Us</h3>
                    <div className="social-links">
                      <a href={CONTACT_INFO.SOCIAL.FACEBOOK} target="_blank" rel="noopener noreferrer">Facebook</a>
                      <a href={CONTACT_INFO.SOCIAL.INSTAGRAM} target="_blank" rel="noopener noreferrer">Instagram</a>
                      <a href={CONTACT_INFO.SOCIAL.TWITTER} target="_blank" rel="noopener noreferrer">Twitter</a>
                      <a href={CONTACT_INFO.SOCIAL.LINKEDIN} target="_blank" rel="noopener noreferrer">LinkedIn</a>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="contact-form-container">
              <form className="contact-form" ref={form} onSubmit={handleSubmit}>
                <h2>Send Us a Message</h2>
                
                <div className="form-group">
                  <label htmlFor="name">Full Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email Address *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="phone">Phone Number *</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="subject">Subject *</label>
                  <select
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select a subject</option>
                    <option value="general">General Inquiry</option>
                    <option value="booking">Ad Booking Support</option>
                    <option value="pricing">Pricing Question</option>
                    <option value="technical">Technical Issue</option>
                    <option value="feedback">Feedback</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="message">Message *</label>
                  <textarea
                    id="message"
                    name="message"
                    rows="5"
                    value={formData.message}
                    onChange={handleChange}
                    required
                  ></textarea>
                </div>

                <button type="submit" className="submit-btn" disabled={isSending}>
                  {isSending ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default ContactUs;
